import React, { useState, useRef, useEffect, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, Check, X } from 'lucide-react';
import clsx from 'clsx';

function formatUserLabel(u) {
  if (!u) return '';
  const name = u.firstName && u.lastName ? `${u.firstName} ${u.lastName}`.trim() : u.username;
  return u.email ? `${name} · ${u.email}` : name;
}

function userSearchHaystack(u) {
  return [u.firstName, u.lastName, u.username, u.email].filter(Boolean).join(' ').toLowerCase();
}

/**
 * Wybór użytkownika z wyszukiwaniem (styl zgodny z formularzami Farm Connect).
 * Lista renderowana przez portal (fixed) — nie jest obcinana przez overflow rodziców.
 */
export default function SearchableUserSelect({
  users = [],
  value,
  onChange,
  placeholder = '— Wybierz osobę —',
  clearLabel = '— Wybierz przełożonego —',
  disabled,
  emptyText = 'Brak pasujących osób',
  searchPlaceholder = 'Szukaj po imieniu, nazwisku, e-mailu…',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const selected = useMemo(() => users.find((u) => u._id === value), [users, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => userSearchHaystack(u).includes(q));
  }, [users, query]);

  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 8;
    const w = r.width;
    const left = Math.min(Math.max(margin, r.left), window.innerWidth - w - margin);
    setDropPos({ top: r.bottom + 4, left, width: w });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setDropPos({ top: 0, left: 0, width: 0 });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const t = e.target;
      if (triggerRef.current?.contains(t) || dropdownRef.current?.contains(t)) return;
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const toggle = () => {
    if (disabled) return;
    setOpen((o) => !o);
  };

  const handleSelect = (id) => {
    onChange(id);
    setOpen(false);
    setQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
  };

  const dropdown =
    open && !disabled && dropPos.width > 0 ? (
      <div
        ref={dropdownRef}
        style={{
          position: 'fixed',
          top: dropPos.top,
          left: dropPos.left,
          width: dropPos.width,
          zIndex: 10000,
        }}
        className="flex max-h-[min(18rem,70vh)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl ring-1 ring-black/10"
        role="listbox"
      >
        <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50/90 px-3 py-2.5">
          <Search className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
          <input
            ref={searchInputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="min-w-0 flex-1 border-0 bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
            onMouseDown={(e) => e.stopPropagation()}
            autoComplete="off"
          />
        </div>
        <ul className="max-h-56 overflow-y-auto py-1">
          <li role="presentation">
            <button
              type="button"
              role="option"
              onClick={() => handleSelect('')}
              className={clsx(
                'w-full px-4 py-2.5 text-left text-sm text-gray-500 hover:bg-gray-50',
                !value && 'bg-slate-50 font-medium text-gray-700'
              )}
            >
              {clearLabel}
            </button>
          </li>
          {filtered.length === 0 ? (
            <li className="px-4 py-3 text-center text-sm text-gray-500">{emptyText}</li>
          ) : (
            filtered.map((u) => {
              const isSel = u._id === value;
              return (
                <li key={u._id} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSel}
                    onClick={() => handleSelect(u._id)}
                    className={clsx(
                      'flex w-full items-start gap-2 px-4 py-2.5 text-left text-sm',
                      isSel
                        ? 'bg-blue-50 font-medium text-blue-900'
                        : 'text-gray-900 hover:bg-gray-50'
                    )}
                  >
                    {isSel ? (
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" aria-hidden />
                    ) : (
                      <span className="mt-0.5 inline-block h-4 w-4 shrink-0" aria-hidden />
                    )}
                    <span className="min-w-0 flex-1 break-words leading-snug">{formatUserLabel(u)}</span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    ) : null;

  return (
    <div className="relative w-full">
      <div
        ref={triggerRef}
        className={clsx(
          'flex w-full items-stretch rounded-xl border-0 bg-white/80 text-sm font-medium shadow-sm ring-1 ring-gray-200/80 transition-shadow',
          !disabled && 'focus-within:ring-2 focus-within:ring-blue-500/40 focus-within:ring-offset-2 focus-within:ring-offset-transparent',
          disabled && 'bg-gray-100 text-gray-500'
        )}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={toggle}
          className={clsx(
            'min-w-0 flex-1 px-4 py-3 text-left outline-none rounded-l-xl',
            disabled && 'cursor-not-allowed'
          )}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <span className={clsx('block truncate', !selected && 'text-gray-500')}>
            {selected ? formatUserLabel(selected) : placeholder}
          </span>
        </button>
        {selected && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="shrink-0 px-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-none border-l border-gray-100"
            title="Wyczyść wybór"
            aria-label="Wyczyść wybór"
          >
            <X className="mx-auto h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          disabled={disabled}
          onClick={toggle}
          className="shrink-0 px-3 text-gray-400 hover:text-gray-600 border-l border-gray-100 rounded-r-xl disabled:opacity-50"
          aria-label={open ? 'Zamknij listę' : 'Otwórz listę'}
        >
          <ChevronDown className={clsx('h-4 w-4 transition-transform', open && 'rotate-180')} />
        </button>
      </div>

      {dropdown && createPortal(dropdown, document.body)}
    </div>
  );
}
